import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Grid, Paper } from "@material-ui/core";
import Condition from "Component/Condition";
import VirtualaizedTable, { ColumnData } from "Common/Component/VirtualizedTable";
import { callWebApi } from "Common/Utility/Api";
import { useMessageBox } from "Hooks/useMessageBox";
import { useGenericStyles } from "Common/Utility/Styles";
import { CancelTokenSource } from "axios";
import { LoadingMode, useLoadingElement } from "Component/Loading";
import { useExecute, useFetch } from "Hooks/useFetch";
import { deleteButton, editButton, referenceButton, useIsValidMenuEditAuthority } from "Common/Utility/AppUtility";
import { useContentHeight } from "Hooks/useResize";
import { MenuIndex } from "Component/Menu";
import { useInputManager } from "Common/Utility/HandleUtility";
import TextField from "Component/TextField";
import { useOnCloseEditDialog } from "Hooks/useOnCloseEditDialog";
import { Design, VisibleColumn } from "Common/Utility/Constants";
import { useColumnControl } from "Hooks/useColumnControl";
import { EditDialog } from "./EditDialog";
import { RepairParts } from "Models/RepairParts";
import { generalComparsion } from "Common/Utility/GenericInterface";

const useStyles = makeStyles((theme) => ({
  loading: {
    width: "100%",
    height: (props: any) => props.height,
    minHeight: 300,
  },
  paper: {
    width: "100%",
    height: (props: any) => props.height,
  },
}));

interface Condition {
  // メーカー
  maker: string;

  // 品番
  productNumber: string;

  // 品名
  name: string;
}

const initialSearchCondition: Condition = {
  maker: "",
  productNumber: "",
  name: "",
};

export const RepairPartsPanel = React.memo(() => {
  const [height, conditionRef] = useContentHeight(Design.componentUnitHeight + Design.margin);

  const classes = useStyles({ height: height });

  const genericClasses = useGenericStyles();

  const message = useMessageBox();

  const [condition, setCondition] = useState<Condition>({ ...initialSearchCondition });

  const [search, setSearch] = useState<Condition>({ ...initialSearchCondition });

  const [data, setData] = useState<RepairParts[]>([]);

  const [editData, setEditData] = useState<RepairParts | undefined>(undefined);

  const inputManager = useInputManager(setCondition);

  const fetchResultLoad = useFetch(
    useCallback(
      async (signal: CancelTokenSource) => {
        const response = await callWebApi().get("/RepairParts", {
          cancelToken: signal.token,
          params: search,
        });

        if (response.data == null) {
          return;
        }

        setData(response.data);
      },
      [search]
    ),
    false
  );

  const handleOnClickSearch = useCallback(() => setSearch({ ...condition }), [condition]);

  const handleOnClickClear = useCallback(() => setCondition({ ...initialSearchCondition }), []);

  const handleOnClickAdd = useCallback(() => {
    setEditData({
      id: null,
      maker: "",
      productNumber: "",
      name: "",
      amount: null,
    });
  }, []);

  const handleOnClickEdit = useCallback(
    (repairParts: RepairParts, columnData: ColumnData, rowIndex: number, columnIndex: number) => {
      setEditData({ ...repairParts });
    },
    []
  );

  const executeDelete = useExecute(
    useCallback(async (unmounted: { value: boolean }, object: { id: string; rowIndex: number }) => {
      await callWebApi().delete(`/repairParts/${object.id}`);

      if (unmounted.value) {
        return;
      }

      setData((value) => {
        value.splice(object.rowIndex, 1);
        return [...value];
      });
    }, [])
  );

  const handleOnClickDelete = useCallback(
    async (repairParts: RepairParts, columnData: ColumnData, rowIndex: number, columnIndex: number) => {
      if (!(await message.confirm("削除確認", "修理部品を削除します。よろしいですか？"))) {
        return;
      }

      if (repairParts.id) {
        executeDelete({ id: repairParts.id, rowIndex: rowIndex });
      }
    },
    [message, executeDelete]
  );

  const handleOnClose = useOnCloseEditDialog("RepairParts", setEditData, setData, "id");

  const isAssetMenuEditValid = useIsValidMenuEditAuthority(MenuIndex.Asset);

  const [getVisible, handleOnCloseContextMenu, handleOnChangeHeaderVisible] = useColumnControl(
    VisibleColumn.RepairParts
  );

  const columns: ColumnData[] = useMemo(() => {
    const ret: ColumnData[] = [];
    if (isAssetMenuEditValid) {
      ret.push(editButton, deleteButton);
    } else {
      ret.push(referenceButton);
    }

    ret.push(
      {
        width: 200,
        label: "メーカー",
        dataKey: "maker",
        headerAlign: "center",
        bodyAlign: "left",
        visible: getVisible(0),
      },
      {
        width: 200,
        label: "品番",
        dataKey: "productNumber",
        headerAlign: "center",
        bodyAlign: "left",
        visible: getVisible(1),
      },
      {
        width: 300,
        label: "品名",
        dataKey: "name",
        headerAlign: "center",
        bodyAlign: "left",
        fit: true,
        visible: getVisible(2),
      },
      {
        width: 200,
        label: "価格",
        dataKey: "amount",
        headerAlign: "center",
        bodyAlign: "right",
        convert: (data: any, rowData: RepairParts) => (rowData.amount == null ? "" : data.toLocaleString() + " 円"),
        visible: getVisible(3),
        sort: (asc: boolean) => {
          setData((value) => {
            value.sort((a, b) => generalComparsion(a.amount, b.amount) * (asc ? 1 : -1));
            return [...value];
          });
        },
      }
    );

    return ret;
  }, [isAssetMenuEditValid, getVisible]);

  return (
    <Grid container direction="row" justify="center" alignItems="flex-start" spacing={1}>
      <Grid item xs={12}>
        <Condition observer={conditionRef}>
          <>
            <TextField
              className={genericClasses.margin}
              label="メーカー"
              value={condition.maker}
              onChange={inputManager.handleOnChange("maker")}
            />
            <TextField
              className={genericClasses.margin}
              label="品番"
              value={condition.productNumber}
              onChange={inputManager.handleOnChange("productNumber")}
            />
            <TextField
              className={genericClasses.margin}
              label="品名"
              value={condition.name}
              onChange={inputManager.handleOnChange("name")}
            />
            <Button className={genericClasses.margin} variant="contained" color="primary" onClick={handleOnClickSearch}>
              検索
            </Button>
            <Button className={genericClasses.margin} variant="outlined" onClick={handleOnClickClear}>
              クリア
            </Button>
          </>
        </Condition>
      </Grid>
      <Grid item xs={12}>
        {useLoadingElement(classes.loading, LoadingMode.Circular, fetchResultLoad) ?? (
          <Paper className={classes.paper}>
            <VirtualaizedTable
              values={data}
              setValues={setData}
              tableHeight={height}
              rowHeight={48}
              columns={columns}
              onClickAdd={isAssetMenuEditValid ? handleOnClickAdd : undefined}
              onClickEdit={handleOnClickEdit}
              onClickReference={handleOnClickEdit}
              onClickDelete={handleOnClickDelete}
              onCloseContextMenu={handleOnCloseContextMenu}
              onChangeHeaderVisible={handleOnChangeHeaderVisible}
              headerContext
            />
          </Paper>
        )}
      </Grid>
      {editData != null && (
        <EditDialog open={true} data={editData} onClose={handleOnClose} editAuth={isAssetMenuEditValid} />
      )}
    </Grid>
  );
});
